import { inject } from '@angular/core';
import { apiFilter } from '@kinet-open-network/client/core/api-client';
import {
  Document,
  DocumentsService,
} from '@kinet-open-network/client/documents/data-access';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withModel,
  withModelCrud,
} from '@kinet-open-network/client/shared/data-access/state';
import {
  ISendDocumentOptions,
  SendDocMethod,
} from '@kinet-open-network/shared/data-access/model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

export const CustomerDocumentsStore = signalStore(
  { protectedState: false },
  withCallState(),
  withModel<Document>(),
  withModelCrud(DocumentsService),
  withState<{ data: Document[] }>({ data: [] }),
  withMethods((store, service = inject(DocumentsService)) => ({
    loadByCustomerId: async (customerId: number) => {
      patchState(store, setLoading());

      try {
        if (!customerId) {
          throw new Error('Customer ID is required');
        }

        const data = await service.queryAsync({
          filters: apiFilter<Document>().customerId(customerId),
          sort: ['-createdAt'],
          perPage: -1,
        });

        patchState(store, { data: data.data }, setLoaded());

        return data.data;
      } catch (error) {
        patchState(store, setError(error));
        throw error;
      }
    },
    signDocument: (id: string, signature: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.signDocument(id, signature), {
        next: (doc) => patchState(store, { model: doc }, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    markAsSigned: (id: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.markAsSigned(id), {
        next: (doc) => patchState(store, { model: doc }, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    generate: (id: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.generate(id), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    send: async (
      id: string,
      method: SendDocMethod,
      options?: ISendDocumentOptions,
    ) => {
      patchState(store, setLoading());

      try {
        await firstValueFrom(service.sendDocument(id, method, options));
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
        throw error;
      }
    },
    upload(customerId: number, data: string, name: string) {
      patchState(store, setLoading());
      return callApiMethod(() => service.upload(customerId, data, name), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
  })),
);
